<script setup lang="ts">
/**
 * PwaInstallBanner — нативное PWA install prompt + iOS Safari fallback
 * (inbox-293 / Task 13 mobile-tailwind plan).
 *
 * Поток:
 *   1. На каждом mount инкрементим visit_count в localStorage.
 *   2. При visit_count >= 2 (и не standalone, и не dismissed <30 дней назад)
 *      → показываем banner.
 *   3. Android/Chrome: ловим beforeinstallprompt, preventDefault, сохраняем
 *      event'а. Тап «Установить» → event.prompt(); если accepted — hide.
 *   4. iOS Safari (нет API): UA-sniff показывает инструкцию «Share → На
 *      экран Домой».
 *   5. Крестик dismiss'ит на 30 дней (localStorage timestamp).
 *   6. appinstalled event → hide + dismiss на 1 год (не показывать снова).
 */

import { onMounted, ref } from 'vue';
import { XIcon, DownloadIcon, Share2Icon } from 'lucide-vue-next';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const visible = ref(false);
const isIos = ref(false);
const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);

const VISIT_KEY = 'helmix_visit_count';
const DISMISS_KEY = 'helmix_install_dismissed_at';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function shouldShow(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return false;
  const count = parseInt(localStorage.getItem(VISIT_KEY) || '0', 10);
  if (count < 2) return false;
  const dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
  if (dismissed && Date.now() - dismissed < DISMISS_TTL_MS) return false;
  return true;
}

function dismiss(): void {
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
  visible.value = false;
}

async function install(): Promise<void> {
  if (!deferredPrompt.value) return;
  await deferredPrompt.value.prompt();
  const choice = await deferredPrompt.value.userChoice;
  if (choice.outcome === 'accepted') visible.value = false;
  deferredPrompt.value = null;
}

onMounted(() => {
  if (typeof window === 'undefined') return;

  const c = parseInt(localStorage.getItem(VISIT_KEY) || '0', 10);
  localStorage.setItem(VISIT_KEY, String(c + 1));

  const ua = navigator.userAgent;
  // iOS detection: iPad/iPhone/iPod, кроме старого Edge mobile (MSStream).
  const iOS = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
  isIos.value = iOS;

  if (iOS) {
    // iOS Safari нет beforeinstallprompt — сразу показываем banner с
    // инструкцией если shouldShow().
    if (shouldShow()) visible.value = true;
  } else {
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
      if (shouldShow()) visible.value = true;
    });
  }

  window.addEventListener('appinstalled', () => {
    visible.value = false;
    // 1 год dismiss — не показывать снова после успешной установки.
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 365 * 24 * 60 * 60 * 1000));
  });
});
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0"
  >
    <div
      v-if="visible"
      class="pwa-banner lg:hidden"
      data-testid="pwa-install-banner"
    >
      <div v-if="isIos" class="pwa-body">
        <p class="pwa-title">Установите как приложение</p>
        <p class="pwa-sub">
          Нажмите <Share2Icon class="pwa-inline-icon" /> внизу Safari → «На экран „Домой“»
        </p>
      </div>

      <div v-else class="pwa-body">
        <p class="pwa-title">Установить link-platform</p>
        <p class="pwa-sub">Быстрый доступ с главного экрана</p>
        <button type="button" class="pwa-install-btn" @click="install">
          <DownloadIcon class="pwa-inline-icon" />
          Установить
        </button>
      </div>

      <button
        type="button"
        class="pwa-close"
        aria-label="Закрыть"
        @click="dismiss"
      >
        <XIcon class="pwa-icon-x" />
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.pwa-banner {
  position: fixed;
  bottom: calc(80px + env(safe-area-inset-bottom));
  left: 8px;
  right: 8px;
  z-index: 30;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: var(--bg-card, #11161e);
  border: 1px solid var(--border, #1f2733);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}
.pwa-body { flex: 1; min-width: 0; }
.pwa-title { font-size: 14px; font-weight: 600; color: var(--fg, #e8edf3); margin: 0; }
.pwa-sub {
  font-size: 12.5px;
  color: var(--fg-muted, #8b95a5);
  margin: 4px 0 0;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.pwa-inline-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  vertical-align: -3px;
}
.pwa-install-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 14px;
  background: var(--accent, #4f8cff);
  color: #fff;
  border: 0;
  border-radius: 8px;
  font: inherit;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  min-height: 36px;
}
.pwa-install-btn:hover { filter: brightness(1.08); }
.pwa-close {
  background: transparent;
  border: 0;
  color: var(--fg-muted, #8b95a5);
  padding: 4px;
  cursor: pointer;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  min-width: 32px;
  min-height: 32px;
  border-radius: 6px;
}
.pwa-close:hover { color: var(--fg, #e8edf3); background: var(--bg-hover, rgba(255,255,255,0.04)); }
.pwa-icon-x { width: 18px; height: 18px; }
</style>
